import { z } from "zod";

import type {
  ClauseRequest,
  PublishRequest,
} from "@/shared/domain/boards/boardAuthoringService";
import {
  INTERACTION_ENTITY_TYPES,
  type InteractionValue,
} from "@/shared/domain/boards/interactionTypes";
import {
  instantNanoseconds,
  parseInteractionValue,
  serializedInteractionValueSchema,
} from "@/shared/domain/boards/interactionValueCodec";
import {
  groupDimensionValueSchema,
  groupClauseSchema,
  groupDimensionMappingSchema,
} from "@/shared/domain/boards/markLineageCodec";
import type {
  MarkLineageRegistrationToken,
  EntityRegistrationToken,
} from "@/shared/domain/boards/markLineageTypes";

import { ApprovalMode } from "./approvalModes";
import { withAuthoringService } from "./boardAuthoringDeps";
import {
  type ClientToolExecutionResult,
  createClientTool,
  toolError,
  ToolErrorKind,
  toolSuccess,
} from "./ClientTools";

export interface PublishBoardClauseInput {
  board_id?: unknown;
  selection?: unknown;
  column?: unknown;
  values?: unknown;
  interval?: unknown;
  label?: unknown;
  clear?: unknown;
  command?: unknown;
}

const revisionSchema = z.strictObject({
  stage: z.string().min(1),
  generation: z.string().min(1),
  schema: z.string().min(1),
});
const commandValueSchema = serializedInteractionValueSchema.transform(
  (value, ctx) => {
    const parsed = parseInteractionValue(value);
    if (parsed.status === "valid") {
      return parsed.value;
    }
    ctx.addIssue({ code: "custom", message: parsed.message });
    return z.NEVER;
  },
);
const keyedValueSchema = z.strictObject({
  key: z.string().min(1),
  value: commandValueSchema,
});
const serializedIntervalSchema = z.strictObject({
  key: z.string(),
  lower: serializedInteractionValueSchema,
  upper: serializedInteractionValueSchema,
  lowerInclusive: z.boolean(),
  upperInclusive: z.boolean(),
});
const serializedClauseSchema = z.union([
  groupClauseSchema,
  z.strictObject({
    kind: z.literal("point"),
    keys: z.array(z.string()),
    values: z.array(z.array(serializedInteractionValueSchema)),
  }),
  z.strictObject({
    kind: z.literal("interval"),
    intervals: z.array(serializedIntervalSchema),
  }),
  z.strictObject({
    kind: z.literal("row"),
    revision: revisionSchema,
    rowKey: z.string().min(1),
  }),
]);
const resultContextSchema = z.strictObject({
  orgId: z.string(),
  instanceId: z.string(),
  definitionDigest: z.string(),
  result: revisionSchema,
  timeRange: z.strictObject({
    startMs: z.number().finite(),
    endMs: z.number().finite(),
  }),
  controlValues: z.record(
    z.string(),
    z.array(serializedInteractionValueSchema),
  ),
  constraints: z.array(
    z.strictObject({
      id: z.string(),
      selection: z.string(),
      scope: z.string(),
      source: z.string(),
      clients: z.array(z.string()),
      resolution: z.enum(["single", "intersect", "crossfilter"]),
      mode: z.enum(["filter", "highlight", "ignore"]),
      keys: z.array(z.strictObject({ key: z.string(), field: z.string() })),
      dimensions: z.array(groupDimensionMappingSchema).optional(),
      clause: serializedClauseSchema,
      publisherExcluded: z.boolean(),
    }),
  ),
  computedFrom: z.strictObject({
    stages: z.array(
      z.strictObject({ stage: z.string(), declarationDigest: z.string() }),
    ),
    fetches: z.array(
      z.strictObject({
        stage: z.string(),
        digest: z.string(),
        fetchedAt: z.number().finite(),
      }),
    ),
  }),
  coverage: z.array(
    z.strictObject({
      stage: z.string(),
      status: z.enum(["complete", "limited", "unknown"]),
      limit: z.number().int().nonnegative().optional(),
      message: z.string().optional(),
    }),
  ),
});
const renderedGroupSchema = z.strictObject({
  markLineageId: z.string().min(1),
  sourceRevision: revisionSchema,
  outputRevision: revisionSchema,
  outputRowKey: z.string().min(1),
  resultContext: resultContextSchema,
  dimensions: z.array(groupDimensionValueSchema),
});
const markLineagePreconditionSchema = z.strictObject({
  kind: z.literal("mark-lineage"),
  markLineageId: z.string().min(1),
  sourceRevision: revisionSchema,
  outputRevision: revisionSchema,
  renderedGeneration: z.string().min(1),
  resultContext: resultContextSchema,
  registration: z
    .string()
    .min(1)
    .transform((value) => value as MarkLineageRegistrationToken),
});
const entitySourceRevisionSchema = z.strictObject({
  source: z.string().min(1),
  generation: z.number().int().nonnegative(),
  path: z.string().min(1),
});
const entityResultContextSchema = resultContextSchema
  .omit({ result: true, computedFrom: true })
  .extend({
    source: entitySourceRevisionSchema,
  });
const entityPreconditionSchema = z.strictObject({
  kind: z.literal("entity"),
  entityType: z.enum(INTERACTION_ENTITY_TYPES),
  entityId: z.string().min(1),
  bindingName: z.string().min(1),
  sourceRevision: entitySourceRevisionSchema,
  resultContext: entityResultContextSchema,
  registration: z
    .string()
    .min(1)
    .transform((value) => value as EntityRegistrationToken),
});
const entitySubjectSchema = z.strictObject({
  kind: z.literal("entity"),
  entityType: z.enum(INTERACTION_ENTITY_TYPES),
  id: commandValueSchema,
});
const commandBaseSchema = z.strictObject({
  instanceId: z.string().min(1),
  definitionDigest: z.string().min(1),
  panelId: z.string().min(1),
  interactionId: z.string().min(1),
  subject: z.discriminatedUnion("kind", [
    z.strictObject({
      kind: z.literal("values"),
      values: z.array(z.array(keyedValueSchema)),
    }),
    z.strictObject({
      kind: z.literal("row"),
      revision: revisionSchema,
      rowKey: z.string().min(1),
      values: z.array(keyedValueSchema),
    }),
    z.strictObject({
      kind: z.literal("group"),
      revision: revisionSchema,
      values: z.array(keyedValueSchema),
      renderedGroup: renderedGroupSchema.optional(),
    }),
    entitySubjectSchema,
    z.strictObject({
      kind: z.literal("interval"),
      intervals: z.array(
        z.strictObject({
          key: z.string().min(1),
          lower: commandValueSchema,
          upper: commandValueSchema,
          lowerInclusive: z.boolean(),
          upperInclusive: z.boolean(),
        }),
      ),
    }),
  ]),
});

// One schema per form of `InteractionRequest`: a stage's result, a chart's mark
// lineage output, and an entity.
const resultCommandSchema = commandBaseSchema.extend({
  resultRevision: revisionSchema,
});
const markLineageCommandSchema = commandBaseSchema.extend({
  resultRevision: revisionSchema,
  precondition: markLineagePreconditionSchema,
});
const entityCommandSchema = commandBaseSchema.extend({
  precondition: entityPreconditionSchema,
  subject: entitySubjectSchema,
});

/**
 * The schema for the form `command` takes, chosen by its precondition: none
 * for a stage's result, `kind: "entity"` for an entity, and any other for a
 * mark lineage output. Parsing against the one form gives each issue the path
 * of the member it is about.
 */
function commandSchemaFor(command: unknown) {
  const precondition =
    typeof command === "object" && command !== null && "precondition" in command
      ? command.precondition
      : undefined;
  if (precondition === undefined) {
    return resultCommandSchema;
  }
  return typeof precondition === "object" &&
    precondition !== null &&
    "kind" in precondition &&
    precondition.kind === "entity"
    ? entityCommandSchema
    : markLineageCommandSchema;
}

/**
 * An ISO 8601 instant with a date, a time, and a zone, which reads the same
 * in every time zone. Its groups are the date with the hours and minutes,
 * the seconds, up to nine digits below the second, and the zone.
 */
const ISO_INSTANT =
  /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(Z|[+-]\d{2}:\d{2})$/;

/** How the tool's description and its error messages word the instants `interval` accepts. */
const ISO_INSTANT_PHRASE =
  "two ISO 8601 instants that each carry a time zone, such as 2026-06-01T00:00:00Z or 2026-06-01T02:00:00+02:00";

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

type Instant = Extract<InteractionValue, { type: "instant" }>;

/**
 * The instant an ISO 8601 text names, counted from the Unix epoch: in
 * milliseconds, or in nanoseconds when the text has digits below the
 * millisecond, so that every digit it gives is kept. Undefined for a text
 * `ISO_INSTANT` does not match, or whose date and time `Date.parse` cannot
 * read, such as a 13th month.
 */
function instantOf(text: string): Instant | undefined {
  const match = ISO_INSTANT.exec(text);
  if (match === null) {
    return undefined;
  }
  const [, hoursAndMinutes, seconds = "00", fraction = "", zone] = match;
  const milliseconds = Date.parse(
    `${hoursAndMinutes}:${seconds}.${fraction.slice(0, 3).padEnd(3, "0")}${zone}`,
  );
  if (!Number.isFinite(milliseconds)) {
    return undefined;
  }
  if (fraction.length <= 3) {
    return {
      type: "instant",
      ticks: BigInt(milliseconds),
      unit: "ms",
      epoch: "unix",
    };
  }
  return {
    type: "instant",
    ticks:
      BigInt(milliseconds) * 1_000_000n +
      BigInt(fraction.slice(3).padEnd(6, "0")),
    unit: "ns",
    epoch: "unix",
  };
}

type IntervalClauseRequest = Extract<
  ClauseRequest,
  { type: "number-interval" | "instant-interval" }
>;

type IntervalReading = { clause: IntervalClauseRequest } | { problem: string };

/** The interval clause on `field` that `value` gives; or what is wrong with `value`. */
function readInterval(field: string, value: unknown): IntervalReading {
  if (!Array.isArray(value) || value.length !== 2) {
    return {
      problem: `interval must be [low, high]: two numbers on a numeric column, or ${ISO_INSTANT_PHRASE} on a time column.`,
    };
  }
  const [low, high] = value as [unknown, unknown];
  const clause = intervalClauseOf(field, low, high);
  if (clause === undefined) {
    return {
      problem: `interval must be two finite numbers, or ${ISO_INSTANT_PHRASE}; never one of each.`,
    };
  }
  if (lowIsAboveHigh(clause)) {
    return {
      problem: "interval's low bound must not be above its high bound.",
    };
  }
  return { clause };
}

/** The interval between two finite numbers or two ISO 8601 instants; undefined for any other pair. */
function intervalClauseOf(
  field: string,
  low: unknown,
  high: unknown,
): IntervalClauseRequest | undefined {
  if (typeof low === "number" && typeof high === "number") {
    return Number.isFinite(low) && Number.isFinite(high)
      ? { type: "number-interval", field, bounds: [low, high] }
      : undefined;
  }
  const lowInstant = typeof low === "string" ? instantOf(low) : undefined;
  const highInstant = typeof high === "string" ? instantOf(high) : undefined;
  return lowInstant === undefined || highInstant === undefined
    ? undefined
    : { type: "instant-interval", field, bounds: [lowInstant, highInstant] };
}

function lowIsAboveHigh(clause: IntervalClauseRequest): boolean {
  if (clause.type === "number-interval") {
    return clause.bounds[0] > clause.bounds[1];
  }
  return (
    instantNanoseconds(clause.bounds[0]) > instantNanoseconds(clause.bounds[1])
  );
}

type RequestReading = { request: PublishRequest } | { problem: string };

/** The authoring service's publish request built from the tool input, or the first thing wrong with the input. */
function readRequest(input: PublishBoardClauseInput): RequestReading {
  if (!isNonEmptyString(input.board_id)) {
    return {
      problem:
        "board_id must be the id of the board on screen, from misc_context.board.id.",
    };
  }
  if (!isNonEmptyString(input.selection)) {
    return { problem: "selection must be 'focus' or 'brush'." };
  }
  if (input.clear !== undefined && typeof input.clear !== "boolean") {
    return { problem: "clear must be true or false." };
  }
  if (input.label !== undefined && typeof input.label !== "string") {
    return { problem: "label must be a string: the entity's display name." };
  }
  const suppliedClauseInputs = [
    input.values !== undefined,
    input.interval !== undefined,
    input.clear === true,
  ].filter(Boolean).length;
  if (suppliedClauseInputs !== 1) {
    return {
      problem:
        "Pass exactly one of values (ids for the focus selection), interval (for the brush selection), or clear true (remove the clause the chat published).",
    };
  }
  const boardId = input.board_id;
  const selection = input.selection;
  if (input.clear === true) {
    if (input.column !== undefined) {
      return {
        problem:
          "clear true removes the clause the chat published into the given selection; omit column, values, and interval.",
      };
    }
    return { request: { boardId, selection, clause: { type: "clear" } } };
  }
  if (!isNonEmptyString(input.column)) {
    return {
      problem:
        "column is required with values or interval: the column the clause is on.",
    };
  }
  const field = input.column;
  const label = input.label === undefined ? {} : { label: input.label };
  if (input.values !== undefined) {
    if (
      !Array.isArray(input.values) ||
      input.values.length === 0 ||
      !input.values.every(isNonEmptyString)
    ) {
      return {
        problem: "values must be a non-empty array of ids, each a string.",
      };
    }
    return {
      request: {
        boardId,
        selection,
        clause: { type: "point", field, values: input.values },
        ...label,
      },
    };
  }
  const interval = readInterval(field, input.interval);
  if ("problem" in interval) {
    return interval;
  }
  return {
    request: { boardId, selection, clause: interval.clause, ...label },
  };
}

/** Runs the panel interaction `input.command` describes, which must be the
 *  call's only input. */
function runPanelInteraction(
  input: PublishBoardClauseInput,
): Promise<ClientToolExecutionResult> | ClientToolExecutionResult {
  if (
    [
      input.board_id,
      input.selection,
      input.column,
      input.values,
      input.interval,
      input.clear,
      input.label,
    ].some((value) => value !== undefined)
  ) {
    return toolError(
      "Send command alone: board_id, selection, column, values, interval, clear and label belong to a call that publishes into the focus or brush selection, which is a separate call.",
      {},
      ToolErrorKind.VALIDATION,
    );
  }
  const parsed = commandSchemaFor(input.command).safeParse(input.command);
  if (!parsed.success) {
    return toolError(
      parsed.error.issues
        .map((issue) => `${issue.path.join(".")}: ${issue.message}`)
        .join(" "),
      {},
      ToolErrorKind.VALIDATION,
    );
  }
  return withAuthoringService((service) => {
    const outcome = service.dispatchInteraction(parsed.data);
    if (outcome.status === "refused") {
      return toolError(
        outcome.diagnostics.map((issue) => issue.message).join(" "),
        { diagnostics: outcome.diagnostics },
        ToolErrorKind.VALIDATION,
      );
    }
    return toolSuccess("executed_panel_interaction", {
      status: outcome.status,
      instance_id: parsed.data.instanceId,
      panel_id: parsed.data.panelId,
      interaction_id: parsed.data.interactionId,
      ...(outcome.status === "committed" ? { sequence: outcome.sequence } : {}),
    });
  });
}

/** Publishes the chat's clause into the focus or brush selection, or clears it. */
function publishOrClearClause(
  input: PublishBoardClauseInput,
): Promise<ClientToolExecutionResult> | ClientToolExecutionResult {
  const reading = readRequest(input);
  if ("problem" in reading) {
    return toolError(reading.problem, {}, ToolErrorKind.VALIDATION);
  }
  const { request } = reading;
  return withAuthoringService((service) => {
    const outcome = service.publish(request);
    switch (outcome.status) {
      case "not-on-screen": {
        return toolError(
          `Board "${outcome.boardId}" is not on screen in this tab, and the chat can publish into the focus or brush selection only on the board the user is looking at. misc_context.board names the one on screen; ask the user to open the board if it is another.`,
          { board_id: outcome.boardId },
          ToolErrorKind.VALIDATION,
        );
      }
      case "selection-not-accepted": {
        const accepted = outcome.selections
          .map((name) => `'${name}'`)
          .join(" or ");
        return toolError(
          `selection "${outcome.selection}" is not a selection the chat can publish into; selection must be ${accepted}.`,
          { selection: outcome.selection, selections: outcome.selections },
          ToolErrorKind.VALIDATION,
        );
      }
      case "published": {
        return toolSuccess("published_board_clause", {
          board_id: request.boardId,
          selection: outcome.selection,
          source: outcome.source,
          clauses: outcome.clauses,
        });
      }
      case "cleared": {
        return toolSuccess("cleared_board_clause", {
          board_id: request.boardId,
          selection: outcome.selection,
          removed: outcome.removed,
          clauses: outcome.clauses,
        });
      }
    }
  });
}

async function execute(
  input: PublishBoardClauseInput,
): Promise<ClientToolExecutionResult> {
  return input.command !== undefined
    ? runPanelInteraction(input)
    : publishOrClearClause(input);
}

const TAGGED_VALUE_INPUT = {
  type: "object",
  description:
    "A tagged value, as client_create_board's schema declares it at " +
    "stages[].body.domain.options[].value. Copy it from the read result; " +
    "never retag a rounded number as an integer.",
};

const KEYED_VALUE_LIST_DESCRIPTION =
  'a list of {"key": "<a key of the interaction\'s subject>", "value": <the ' +
  "row's tagged value for the field that key names>}";

// Advertises where each member of `command` goes. It lists no members for the
// objects the model copies from a read result (a result revision, a
// precondition, a rendered group); `commandSchemaFor` checks those in full.
const commandInputSchema = {
  type: "object",
  required: [
    "instanceId",
    "definitionDigest",
    "panelId",
    "interactionId",
    "subject",
  ],
  description:
    "Runs one interaction that a panel of the board on screen declares. Build " +
    "it from a client_read_board call with board_id, panel_id and rows, " +
    "made in this turn: the tool refuses a command whose instance or " +
    "result has since changed. instanceId and definitionDigest are on " +
    "every interaction_results entry of that read; a client_read_board " +
    "call with board_id alone also returns them, under `on_screen_board`, " +
    "while the board is on screen. misc_context.board does not carry " +
    "them. For a row of a stage, " +
    "resultRevision is the `revision` of the interaction_results entry " +
    "for the interaction's binding. For a mark a chart computes (an interaction whose " +
    "subject has a markLineageId, the id of the chart's mark lineage, " +
    "which declares the source rows each drawn mark stands for), send the " +
    "`precondition` of that mark lineage's markLineageOutputs entry " +
    "whole, with precondition.outputRevision as resultRevision; the board " +
    "refuses the interaction when the precondition no longer matches the " +
    'result on screen. A precondition of kind "entity" is sent whole ' +
    'with an "entity" subject and no resultRevision. When the read ' +
    "returned no such entry the interaction cannot be run; never make up a " +
    "revision, a row key or a precondition.",
  properties: {
    instanceId: { type: "string" },
    definitionDigest: { type: "string" },
    panelId: { type: "string" },
    interactionId: { type: "string" },
    resultRevision: {
      type: "object",
      description: "{stage, generation, schema}, copied unchanged.",
    },
    precondition: {
      type: "object",
      description: "Copied unchanged from the read result.",
    },
    subject: {
      type: "object",
      required: ["kind"],
      description:
        "What the interaction is run on, in the kind the interaction's own subject " +
        `declares, with only that kind's members. "row": revision, rowKey ` +
        `and values, ${KEYED_VALUE_LIST_DESCRIPTION}. "group": revision ` +
        "and values the same way, plus `renderedGroup` when the markLineageOutputs " +
        'row carries one. "values": values as a list of such lists, one ' +
        'per selected item. "entity": entityType and id (a tagged value). ' +
        '"interval": intervals, each {key, lower, upper, lowerInclusive, ' +
        "upperInclusive} with tagged values for the bounds.",
      properties: {
        kind: {
          type: "string",
          enum: ["values", "row", "group", "entity", "interval"],
        },
        values: { type: "array" },
        revision: {
          type: "object",
          description: "The same object as resultRevision.",
        },
        rowKey: {
          type: "string",
          description:
            "For a stage's row, its rowKey in interaction_results. For a " +
            "chart's computed row, its outputRowKey in markLineageOutputs, not " +
            "its rowKey. A row whose rowOrigin is " +
            '"imputed" cannot be acted on.',
        },
        renderedGroup: {
          type: "object",
          description:
            "group only: the row's `renderedGroup` object from markLineageOutputs, " +
            "copied unchanged.",
        },
        entityType: { type: "string", enum: [...INTERACTION_ENTITY_TYPES] },
        id: TAGGED_VALUE_INPUT,
        intervals: { type: "array", items: { type: "object" } },
      },
    },
  },
};

/**
 * `client_publish_board_clause`: changes what the board on screen shows for
 * this viewer, without writing the saved definition. One call does one of
 * two things: it publishes the chat's clause into the `focus` or `brush`
 * selection or clears it, or it runs one interaction a panel declares, from a
 * `command` built out of objects a `client_read_board` call for that panel
 * returned.
 *
 * A clause the chat publishes into `focus` or `brush` reaches the panels
 * declaring neither `interactions` nor `selectionBindings`
 * (`mapClauseOntoFields`, `planFetch`). An interval between two instants
 * also reaches a panel whose `selectionBindings` entry for the selection has
 * the column as a key, since `publishClause` keys it by the column's name,
 * as a chart's brush on a time axis is keyed; every other clause the chat
 * publishes is not keyed, so no such panel applies it. The mounted
 * board's interaction runtime refuses a command whose instance id or
 * definition digest is not the board's on screen, or whose result revision
 * or precondition no longer matches the result the panel registered.
 */
export const tool = createClientTool<PublishBoardClauseInput>({
  name: "client_publish_board_clause",
  description:
    "Changes what the board on screen shows for this viewer; nothing is " +
    "saved and no definition changes. " +
    "The board must be on screen in this tab (misc_context.board). One " +
    "call does one of two things. " +
    "(1) Publish into the focus or brush selection: board_id, selection, " +
    "and one of values, interval or clear. selection 'focus' with column " +
    "and values publishes those entity ids into the focus selection; " +
    "selection 'brush' with column and interval publishes that interval " +
    "into the brush selection; clear true removes the clause the chat " +
    "published into the given selection. The clause is published at the " +
    "board and reaches the panels that declare neither interactions nor " +
    "selectionBindings, take part in that selection and sit in no section " +
    "that declares its own selection of that name; such a panel narrows " +
    "when its rows carry that column or its source's fetch filters by it. " +
    "An interval between two instants is keyed by the column, as a " +
    "chart's brush on a time column is, so it also reaches a panel whose " +
    "selectionBindings entry for brush has the column as a key " +
    '(guide chapter "focus-and-brush", section "Publishing into `focus` ' +
    'or `brush` from chat"). The control bar above the canvas lists the ' +
    "clause as the chat's, where the user can remove it. This never " +
    "changes a control. " +
    "(2) An interaction a panel declares, which is how to publish into a " +
    "selection the board's selections declare, set a control, open " +
    "detail rows or navigate: send command alone; the result's status " +
    "says what the interaction did, and a committed result's sequence counts " +
    "the changes to the board's selections and control values since the " +
    "board was shown. " +
    "A publish or clear result's clauses, like misc_context.board.clauses, " +
    "list what is selected and by whom; a time_range clause's bounds are in epoch " +
    "milliseconds, and an instant is a count of ticks in its unit. " +
    "Names are display text and are not unique, so pass ids, which " +
    "clauses, client_read_board's rows, or search_roboto give you.",
  input_schema: {
    type: "object",
    properties: {
      board_id: {
        type: "string",
        description:
          "For the focus or brush selection: the board on screen, from misc_context.board.id.",
      },
      selection: {
        type: "string",
        description:
          "Which selection to publish into: 'focus' selects entities by " +
          "their id column; 'brush' narrows to an interval of a " +
          "numeric or time column.",
      },
      column: {
        type: "string",
        description:
          "The column the clause is on. For focus, the id " +
          "column of the kind of entity the board shows (session_id on a " +
          "board of sessions); for brush, the numeric or time column " +
          "the panels share.",
      },
      values: {
        type: "array",
        items: { type: "string" },
        description: "For focus: one or more ids, as strings.",
      },
      interval: {
        type: "array",
        minItems: 2,
        maxItems: 2,
        description:
          "For brush: [low, high], both ends included. On a numeric " +
          "column, two finite numbers. On a time column, " +
          `${ISO_INSTANT_PHRASE}, with up to nine digits below the ` +
          "second. Never one of each.",
      },
      label: {
        type: "string",
        description:
          "Optional: the entity's display name (a session's name), shown " +
          "in place of the ids on the clause's entry in the control bar.",
      },
      clear: {
        type: "boolean",
        description:
          "true removes the clause the chat published into the given " +
          "selection and nothing else; omit column, values, and interval.",
      },
      command: commandInputSchema,
    },
  },
  execute,
  defaultApprovalMode: ApprovalMode.AUTO_EXECUTE,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Interact with a board",
  displayDescription:
    "Changes selections or controls, opens details, or navigates using the board on screen. The saved board definition stays unchanged.",
});
